import { deserializeBlob } from '@heidi/capture/utils';
import { getLogDictationsEnabled } from '@heidi/workspace/data';

import { type Format } from '@heidi/capture/contracts';
import { type DictationChunk } from '@heidi/capture/types';
import { type LanguageCode } from '@heidi/schema/ml-scribe';
import { AxiosError } from 'axios';
import { get, round } from 'lodash-es';
import { t11nHttp } from '../../../data/api/t11nHttp';
import { getDictationBubblePorts } from '../bubble-ports';
import { type UserContext } from './buildUserContext';

export interface LastChunkContext {
  accumulatedText: string;
  userContext: UserContext;
  /** User's verbal-commands preference; the backend default applies if omitted. */
  verbalCommandsEnabled?: boolean;
}

/**
 * Discriminated union so TypeScript enforces:
 * - `lastChunkContext` is required when `isLastChunk: true`
 * - no context fields are allowed when `isLastChunk` is false/absent
 */
type TranscribeChunkOptions =
  | { isLastChunk?: false }
  | { isLastChunk: true; lastChunkContext: LastChunkContext };

export interface ProcessedResult {
  text: string;
  processed?: string | null;
  post_processing_model?: string | null;
}

export async function transcribeChunk(
  chunk: DictationChunk,
  format: Format,
  languages?: LanguageCode[],
  useLogsBackend: boolean = false,
  options: TranscribeChunkOptions = {}
): Promise<ProcessedResult> {
  const { index, duration, blob: blobSerialized } = chunk;
  if (!blobSerialized) throw new Error();
  const formData = new FormData();
  const blob = deserializeBlob(blobSerialized)!;
  formData.append('file', blob, `chunk-${index + 1}.${format}`);
  formData.append('chunk_index', String(index));
  formData.append('duration', String(round(duration * 1000)));
  languages?.forEach((lang) => formData.append('languages[]', lang));
  formData.append('is_desktop', 'false');
  if (getDictationBubblePorts().isDictationLogOptOutEnabled()) {
    formData.append('log_dictations_enabled', String(getLogDictationsEnabled()));
  }

  if (options.isLastChunk) {
    const ctx = options.lastChunkContext;
    formData.append('is_last_chunk', 'true');
    formData.append(
      'last_chunk_context',
      JSON.stringify({
        accumulated_text: ctx.accumulatedText,
        user_context: ctx.userContext,
        ...(ctx.verbalCommandsEnabled !== undefined && {
          verbal_commands_enabled: ctx.verbalCommandsEnabled,
        }),
      })
    );
  }

  if (useLogsBackend) {
    formData.append('dictation_id', chunk.dictationId);
    formData.append('dictation_recording_id', chunk.dictationRecordingId);
    formData.append('chunk_produced_at', new Date(chunk.producedAt).toISOString());
  }

  const client = useLogsBackend
    ? await getDictationBubblePorts().loadT11nHttpV2()
    : t11nHttp;

  try {
    const { data } = await client.post<ProcessedResult>(
      `/transcribe-chunk-dictation`,
      formData
    );

    return {
      text: data.text,
      processed: data.processed,
      post_processing_model: data.post_processing_model,
    };
  } catch (err) {
    if (err instanceof AxiosError) {
      // Handle 422 and FileTooSmall errors gracefully without retrying
      if (
        err.response?.status === 422 ||
        get(err.response?.data, 'code') === 'FileTooSmall'
      ) {
        return { text: '', processed: null, post_processing_model: undefined };
      }
    }

    throw err;
  }
}
